import { useState, useEffect, useRef } from "react";
import Icon from "../Icon";
import * as api from "../../api";
import { sonidoActivo, alternarSonido, reproducirAviso } from "../../utils";
import { clicable } from "../ClicableTeclado.jsx";
import { diaDeAvisoFichaje } from "../../avisoFichaje";
import { idEncargoDeAviso } from "../../avisoEncargo";

// «notion-fuente:<id de la lista>:<ms de la pasada>» (T-142): el aviso del modo automatico; el instante es el que pide «Deshacer»
const avisoFuente = tipo => {
  const m = /^notion-fuente:(\d+):(\d+)$/.exec(String(tipo || ""));
  return m ? { fuente_id: m[1], desde: new Date(Number(m[2])).toISOString() } : null;
};

// Campana de la cabecera.
//
// Mezcla DOS fuentes que no se parecen en nada:
//   · las notificaciones propias de ALM, que viven en Postgres y se marcan
//     leídas contra la API;
//   · las incidencias nuevas de Odoo, que solo existen en el navegador de cada
//     usuario (ver useTicketsNuevos) y se marcan leídas en localStorage.
//
// Por eso cada fila lleva su propio comportamiento al pulsarla y al borrarla, en
// vez de un manejador único.
export default function CampanaNotificaciones({ notifs, setNotifs, avisosTickets, onAbrirTicket, onAbrirFichaje, onAbrirEncargo }) {
  const [abierta, setAbierta] = useState(false);
  const [conSonido, setConSonido] = useState(sonidoActivo());
  const [deshaciendo, setDeshaciendo] = useState(null);   // id del aviso mientras se deshace
  const [deshechas, setDeshechas] = useState({});         // id -> texto del resultado
  const caja = useRef(null);
  const relojHover = useRef(null);

  useEffect(() => {
    if (!abierta) return;
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) setAbierta(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierta]);

  // Las incidencias se normalizan a la forma de una notificación de ALM y se
  // ordenan por fecha con las demás: para quien mira la campana son lo mismo.
  const todas = [
    ...avisosTickets.historial.map(t => ({
      id: `tk-${t.id}`, _ticket: t, read: !!t.leido,
      message: `Incidencia ${t.numero}: ${t.titulo}`,
      created_at: t.creado,
    })),
    ...notifs,
  ].sort((a, b) => new Date(b.created_at || b.ts || 0) - new Date(a.created_at || a.ts || 0));

  const sinLeer = todas.filter(n => !n.read).length;

  const marcarLeida = n => {
    if (n.read) return;
    if (n._ticket) avisosTickets.marcarLeido(n._ticket.id);
    else {
      api.markNotifRead(n.id).catch(() => {});
      setNotifs(p => p.map(x => (x.id === n.id ? { ...x, read: true } : x)));
    }
  };

  // Al pasar el cursor por encima se marca leida, pero con medio segundo de
  // permanencia: sin ese retardo, cruzar la lista con el raton para llegar
  // abajo las marcaba todas de golpe.
  const alEntrar = n => {
    clearTimeout(relojHover.current);
    if (!n.read) relojHover.current = setTimeout(() => marcarLeida(n), 500);
  };
  const alSalir = () => clearTimeout(relojHover.current);

  useEffect(() => () => clearTimeout(relojHover.current), []);

  const pulsar = n => {
    marcarLeida(n);
    if (n._ticket) onAbrirTicket(n._ticket.id);
    // Un fichaje corregido en Odoo lleva al dia en Asistencias.
    const dia = !n._ticket && onAbrirFichaje ? diaDeAvisoFichaje(n.type) : null;
    if (dia) onAbrirFichaje(dia);
    // Un encargo (recibido, hecho, «no puedo» o comentario) lleva a su hilo.
    const encargo = !n._ticket && onAbrirEncargo ? idEncargoDeAviso(n.type) : null;
    if (encargo) onAbrirEncargo(encargo);
    setAbierta(false);
  };

  const borrar = n => {
    if (n._ticket) avisosTickets.borrar(n._ticket.id);
    else {
      api.deleteNotif(n.id).catch(() => {});
      setNotifs(p => p.filter(x => x.id !== n.id));
    }
  };

  const deshacerAuto = async (n, a) => {
    setDeshaciendo(n.id);
    try {
      const r = await api.deshacerFuentesNotion(a);
      setDeshechas(p => ({ ...p, [n.id]: r.retiradas > 0 ? `Retiradas ${r.retiradas}${r.conservadas > 0 ? `; se conservan ${r.conservadas} que ya habías tocado` : ""}.` : "No había nada sin tocar que retirar." }));
    } catch (e) { setDeshechas(p => ({ ...p, [n.id]: "No se ha podido deshacer. Inténtalo de nuevo." })); }
    finally { setDeshaciendo(null); }
  };

  const marcarTodas = () => {
    api.markAllNotifsRead().catch(() => {});
    setNotifs(p => p.map(n => ({ ...n, read: true })));
    avisosTickets.marcarTodosLeidos();
  };

  return (
    <div style={{ position:"relative" }} ref={caja}>
      <button className="btn" onClick={() => setAbierta(p => !p)}
        style={{ padding:"6px 9px", borderRadius:8, border:"1px solid var(--border)",
                 background: abierta ? "var(--accent-bg)" : "var(--surface)", color:"var(--text2)",
                 cursor:"pointer", display:"flex", alignItems:"center", position:"relative" }}>
        <Icon name="bell" size={15} color={abierta ? "var(--accent)" : "var(--text3)"}/>
        {sinLeer > 0 && (
          <span style={{ position:"absolute", top:-5, right:-5, minWidth:16, height:16, padding:"0 4px",
                         borderRadius:9, background:"var(--danger)", color:"var(--on-danger)", fontSize:10,
                         fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>
            {sinLeer}
          </span>
        )}
      </button>

      {abierta && (
        <div className="si notif-panel"
          style={{ position:"absolute", top:"calc(100% + 8px)", right:0, width:340,
                   background:"var(--surface)", borderRadius:12, boxShadow:"var(--shadow-lg)",
                   border:"1px solid var(--border)", zIndex:200, overflow:"hidden" }}>

          <div style={{ padding:"12px 14px", borderBottom:"1px solid var(--border2)",
                        display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontWeight:700, fontSize:13 }}>Notificaciones</span>
            <button aria-label={conSonido ? "Silenciar el aviso sonoro" : "Activar el aviso sonoro"} className="btn"
              title={conSonido ? "Silenciar el aviso sonoro" : "Activar el aviso sonoro"}
              onClick={() => { const v = alternarSonido(); setConSonido(v); if (v) reproducirAviso(); }}
              style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer",
                       padding:2, display:"flex" }}>
              <Icon name={conSonido ? "megaphone-fill" : "x"} size={12}
                color={conSonido ? "var(--accent)" : "var(--text3)"}/>
            </button>
            <button className="btn" onClick={marcarTodas}
              style={{ fontSize:10, color:"var(--accent)", background:"none", border:"none",
                       cursor:"pointer", fontFamily:"var(--font)" }}>
              Marcar todas leídas
            </button>
          </div>

          <div style={{ maxHeight:"min(320px, 50vh)", overflowY:"auto" }}>
            {todas.length === 0 && (
              <div style={{ padding:24, textAlign:"center", color:"var(--text3)", fontSize:12 }}>
                Sin notificaciones
              </div>
            )}
            {todas.map(n => (
              <div key={n.id} {...clicable(() => pulsar(n))}
                style={{ padding:"10px 14px", borderBottom:"1px solid var(--border2)", cursor:"pointer",
                         background: n.read ? "transparent" : "#f0f9ff", display:"flex",
                         alignItems:"flex-start", gap:10, transition:"background .1s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; alEntrar(n); }}
                onMouseLeave={e => { e.currentTarget.style.background = n.read ? "transparent" : "#f0f9ff"; alSalir(); }}>

                {n._ticket
                  ? <Icon name="headset" size={12} color={n.read ? "#d1d5db" : "var(--red-text)"}
                      style={{ flexShrink:0, marginTop:2 }}/>
                  : <div style={{ width:8, height:8, borderRadius:"50%",
                                  background: n.read ? "#d1d5db" : "var(--accent)",
                                  flexShrink:0, marginTop:4 }}/>}

                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight: n.read ? 400 : 600, lineHeight:1.4 }}>
                    {n.message || n.msg}
                  </div>
                  {!n._ticket && avisoFuente(n.type) && (
                    deshechas[n.id]
                      ? <div role="status" style={{ fontSize:11, color:"var(--text2)", marginTop:4 }}>{deshechas[n.id]}</div>
                      : <button type="button" className="btn" disabled={deshaciendo === n.id}
                          onClick={e => { e.stopPropagation(); deshacerAuto(n, avisoFuente(n.type)); }}
                          style={{ marginTop:5, minHeight:28, padding:"0 10px", borderRadius:8, border:"1px solid var(--border-ctl)",
                                   background:"var(--surface)", color:"var(--text2)", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"var(--font)" }}>
                          Deshacer</button>
                  )}
                  <div style={{ fontSize:10, color:"var(--text3)", marginTop:3 }}>
                    {new Date(n.created_at || n.ts).toLocaleString("es-ES",
                      { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                  </div>
                </div>

                <button aria-label="Borrar notificación" className="btn" onClick={e => { e.stopPropagation(); borrar(n); }}
                  style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex", background:"none", border:"none", cursor:"pointer", padding:2,
                           display:"flex", opacity:.4 }}>
                  <Icon name="x" size={11} color="var(--text3)"/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
